/**
 * Utility functions for PagesContainer components
 * Functions for extracting and processing markup/XML children nodes
 */

import { isSet, getWindowDataUsingId, conditionRulesChecking, getHardCodedIds } from "../../Utils/CommonFunctions";
import * as ShipConstants from '../../Global/Constants';
import { getWindowObjectUsingWindowId } from '../../Utils/UserFunctions';
import {
  applyGetRatesFormatToExecuteValue,
  buildPagesRedirectFieldOutputs,
  normalizePagesGetRatesRedirectOutputs,
  normalizePagesHyperthreadPackages,
  normalizePagesAdditionalServices,
  normalizePagesRatingsShipmentDateForExecute,
  normalizePicklistPackageType,
  packageRowsHaveSubformLookupOrDescription,
  packageRowsHaveDims,
  sanitizePagesAdditionalServices,
} from "./getRatesPagesUtils";
import {
  isLikelyCountryCodeNotPostal,
  isPagesPostalVariableKey,
} from "./pagesAutomationUtils";
import {
  sortPagesSubformRowsLikeLayout,
  filterEmptyPagesPackageSubformRows,
  normalizePagesSubformPackageRows,
  stripPersistedSubformRowIds,
} from "../../Components/MarkupConversion/HyperTextElementBuilder/utils/pagesSubformRows";
import { applyTailgatePresetToServices as applyTailgatePresetToServicesUtil } from "./pagesTailgatePresetUtils";
import { isTruthyPagesFlag } from "./pagesFeatureConfig";

export {
  buildPagesRedirectFieldOutputs,
  normalizePagesGetRatesRedirectOutputs,
  normalizePagesHyperthreadPackages,
  normalizePagesAdditionalServices,
};

const LOOKUP_RECORD_ID_FIELD = "record_id";

/** Query key that identifies which left-nav Pages Window opened this session (unique org_window_id). */
export const PAGES_ORG_WINDOW_QUERY_KEY = "org_window_id";

/**
 * Build ?query for /pages/... routes.
 * Same idea as /layouts/{window_name}/...: identity lives in the URL for left-nav orange.
 * Preserves public/domain from the current location; sets/keeps org_window_id.
 */
export const buildPagesRouteQuery = ({ orgWindowId, mode } = {}) => {
  const current = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const next = new URLSearchParams();
  if (current.get("public") === "true") {
    next.set("public", "true");
    next.set("domain", current.get("domain") || "apps.myship.ca");
  }
  if (mode) {
    next.set("mode", mode);
  }
  const ow = orgWindowId || current.get(PAGES_ORG_WINDOW_QUERY_KEY);
  if (ow) {
    next.set(PAGES_ORG_WINDOW_QUERY_KEY, ow);
  }
  const qs = next.toString();
  return qs ? `?${qs}` : "";
};

export const getPagesOrgWindowIdFromSearch = (search) => {
  if (!search) return null;
  return new URLSearchParams(search).get(PAGES_ORG_WINDOW_QUERY_KEY) || null;
};

/**
 * User-facing message for pages HTTP failures (timeout, network, API error body).
 */
export const resolvePagesHttpErrorMessage = (
  error,
  fallback = "Request failed. Please try again."
) => {
  if (!error) return fallback;
  const code = error.code;
  const msg = typeof error.message === "string" ? error.message : "";
  if (code === "ECONNABORTED" || /timeout/i.test(msg)) {
    return "The request timed out. Please try again.";
  }
  if (!error.response) {
    return "Network error. Please check your connection and try again.";
  }
  const apiMessage =
    error.response?.data?.message ||
    error.response?.data?.error ||
    null;
  if (apiMessage && typeof apiMessage === "string") {
    return apiMessage;
  }
  return msg || fallback;
};

/**
 * Whether a pages config load failure should be treated as not-found.
 */
export const isPagesConfigNotFoundError = (error, response) => {
  const status = error?.response?.status;
  if (status === 404) return true;
  if (response == null) return true;
  if (response?.code != null && response.code !== 200) {
    const message = String(response?.message || "").toLowerCase();
    if (
      message.includes("not found") ||
      message.includes("no data") ||
      response.code === 404
    ) {
      return true;
    }
  }
  return false;
};

const parseFilterVarFields = (filterVar = "") => {
  if (!filterVar || typeof filterVar !== "string") return [];
  return filterVar.split(",").map((s) => s.trim()).filter(Boolean);
};

const isUuidLike = (raw) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(raw == null ? "" : raw).trim()
  );

/** Ensure lookup filter objects expose metatable id + record_id for step SQL. */
const enrichLookupFilterIdentity = (raw) => {
  if (raw == null) return raw;
  const enrichOne = (item) => {
    if (item == null || typeof item !== "object" || Array.isArray(item)) {
      return item;
    }
    const ld =
      item.lookupData && typeof item.lookupData === "object"
        ? item.lookupData
        : null;
    const valueStr =
      item.value != null && String(item.value).trim() !== ""
        ? String(item.value).trim()
        : "";
    // Prefer live lookupData over a possibly stale form `value` (orphan record_id).
    const uuid =
      (item.id != null && String(item.id).trim() !== ""
        ? String(item.id).trim()
        : "") ||
      (ld && ld.id != null && String(ld.id).trim() !== ""
        ? String(ld.id).trim()
        : "") ||
      (isUuidLike(valueStr) ? valueStr : "");
    const recordId =
      (item.record_id != null && String(item.record_id).trim() !== ""
        ? String(item.record_id).trim()
        : "") ||
      (ld && ld.record_id != null && String(ld.record_id).trim() !== ""
        ? String(ld.record_id).trim()
        : "") ||
      (ld && ld.recordId != null && String(ld.recordId).trim() !== ""
        ? String(ld.recordId).trim()
        : "") ||
      (valueStr && !isUuidLike(valueStr) ? valueStr : "") ||
      (valueStr && !uuid ? valueStr : "");
    if (!uuid && !recordId) {
      return item;
    }
    return {
      ...item,
      ...(uuid ? { id: uuid } : {}),
      ...(recordId || uuid ? { value: recordId || uuid } : {}),
    };
  };
  if (Array.isArray(raw)) {
    return raw.map(enrichOne);
  }
  return enrichOne(raw);
};

export const getFilterPayloadValue = (fieldFormPayload, fieldName) => {
  if (!fieldName || !fieldFormPayload) return undefined;
  const entry = fieldFormPayload[fieldName];
  if (entry == null) return undefined;
  const selected =
    typeof entry === "object" && "selected_value" in entry
      ? entry.selected_value
      : entry;
  // Account → Address_lookup filterVar must keep uuid `id` when present.
  if (String(fieldName).toLowerCase() === "account") {
    return enrichLookupFilterIdentity(selected);
  }
  return selected;
};

const formatExecuteSelectedValue = (selectedValue, { keepPlainObject } = {}) => {
  if (Array.isArray(selectedValue)) return selectedValue;
  // Plan C selected_rate must stay a plain Map — wrapping as [obj] breaks
  // SelectedRateShipmentArrayMerge.findSelectedRate (expects Map, not List).
  if (selectedValue && typeof selectedValue === "object") {
    return keepPlainObject ? selectedValue : [selectedValue];
  }
  return selectedValue;
};

const ARRAY_OBJECT_FIELD_TYPES = ["Array", "Object"];
const LOOKUP_FIELD_TYPES = ["Lookup", "MultiLookup"];

/** Pages scm:inputtext type="Checkbox" / CheckBoxes — not layout window fields. */
export const isPagesCheckboxFieldType = (variableMeta) => {
  const fieldType = String(
    variableMeta?.fieldtype_name ??
      variableMeta?.field_type_name ??
      variableMeta?.type_name ??
      variableMeta?.type ??
      ""
  ).toLowerCase();
  return fieldType === "checkbox" || fieldType === "checkboxes";
};

/**
 * PG-SCM-IN-019: untouched pages checkbox has no selected_value.
 * Empty string is not a boolean — treat absent/empty as false. Leave true/false as-is.
 */
export const normalizePagesCheckboxSelectedValue = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    value === "null" ||
    value === "undefined"
  ) {
    return false;
  }
  return value;
};

/** Resolve pages scm:inputtext display/execute value. Checkbox-only default; other types keep "". */
export const resolvePagesInputSelectedValue = (
  type,
  payloadSelected,
  dataElmtStr
) => {
  const hasPayload =
    payloadSelected !== undefined &&
    payloadSelected !== null &&
    payloadSelected !== "null" &&
    payloadSelected !== "undefined";
  const raw = hasPayload ? payloadSelected : dataElmtStr;
  if (isPagesCheckboxFieldType({ type })) {
    return normalizePagesCheckboxSelectedValue(raw);
  }
  return raw ?? "";
};

export const isArrayOrObjectPagesVariable = (variableMeta) => {
  const fieldType = variableMeta?.fieldtype_name ?? variableMeta?.type;
  return ARRAY_OBJECT_FIELD_TYPES.includes(fieldType);
};

export const isLookupPagesVariable = (variableMeta) => {
  if (!variableMeta) return false;
  const fieldType = variableMeta?.fieldtype_name ?? variableMeta?.type;
  if (LOOKUP_FIELD_TYPES.includes(fieldType)) return true;
  return isSet(variableMeta.window_field_id_path, null) !== null;
};

const parseAccidentalJsonString = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return value;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return value;
  }
};

/**
 * Build selected_value for pages-config-execute.
 * STEPS/API: native JSON values (Steps serializes server-side).
 * RATINGS: JSON-stringify only Array/Object variables (HyperThread .toString() workaround).
 * Lookup/MultiLookup/window-field vars: never stringify (by variable metadata, not value shape).
 */
export const formatPagesExecuteSelectedValue = (
  selectedValue,
  variableMeta,
  automationType
) => {
  const apiName = variableMeta?.api_name ?? variableMeta?.variable_name ?? "";
  const isSelectedRate =
    apiName === "selected_rate" || apiName === "var_selected_rate";
  const parsed = parseAccidentalJsonString(selectedValue);
  const finalValue = formatExecuteSelectedValue(parsed, {
    keepPlainObject: isSelectedRate,
  });

  // Never JSON.stringify the rate-card Map (RATINGS Array/Object workaround).
  if (isSelectedRate && finalValue && typeof finalValue === "object") {
    return finalValue;
  }

  return applyGetRatesFormatToExecuteValue(
    finalValue,
    apiName,
    automationType,
    isArrayOrObjectPagesVariable(variableMeta),
    isLookupPagesVariable(variableMeta)
  );
};

export const resolvePagesVariableMeta = (resourcesList, item, apiName) => {
  const pool = [
    ...(resourcesList?.input || []),
    ...(resourcesList?.variables || []),
  ];
  return (
    pool.find((v) => v.id === item.pages_variable_id) ||
    (apiName &&
      (pool.find((v) => v.api_name === apiName) ||
        pool.find((v) => v.variable_name === apiName))) ||
    null
  );
};

const looksLikeJsonContainer = (text) => {
  const trimmed = String(text == null ? "" : text).trim();
  return trimmed.startsWith("[") || trimmed.startsWith("{");
};

const tryParseJsonContainer = (text) => {
  try {
    return JSON.parse(String(text).trim());
  } catch (e) {
    return undefined;
  }
};

const recordIdEntriesFromText = (text) =>
  String(text)
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .map((id) => ({ id }));

/**
 * Array / lookup pages params are sent as [{id}].
 * A JSON array string such as ["222766"] must be parsed first. Wrapping that
 * whole string as one id makes the step search for "222766" (quotes included).
 * Arrays of objects (Package rows, rate cards) are left unchanged.
 * Malformed JSON is returned as-is so it is not rewritten into a clean id.
 */
export const normalizeRecordIdSelectedValue = (raw) => {
  if (raw == null || raw === "") {
    return [];
  }
  if (Array.isArray(raw)) {
    if (raw.some((item) => item != null && typeof item === "object")) {
      return raw;
    }
    const entries = [];
    raw.forEach((item) => {
      if (item == null || item === "") {
        return;
      }
      if (typeof item === "string" && looksLikeJsonContainer(item)) {
        const parsed = tryParseJsonContainer(item);
        if (parsed === undefined) {
          return;
        }
        const normalized = normalizeRecordIdSelectedValue(parsed);
        if (Array.isArray(normalized)) {
          normalized.forEach((entry) => entries.push(entry));
        }
        return;
      }
      recordIdEntriesFromText(item).forEach((entry) => entries.push(entry));
    });
    return entries;
  }
  if (typeof raw === "object") {
    if (Object.prototype.hasOwnProperty.call(raw, "id")) {
      return [raw];
    }
    return raw;
  }
  if (typeof raw !== "string") {
    return raw;
  }
  const trimmed = raw.trim();
  if (trimmed === "") {
    return [];
  }
  if (looksLikeJsonContainer(trimmed)) {
    const parsed = tryParseJsonContainer(trimmed);
    if (parsed === undefined) {
      return raw;
    }
    return normalizeRecordIdSelectedValue(parsed);
  }
  return recordIdEntriesFromText(trimmed);
};

/** True when a mapping carries record ids (lookup / object variables), so a
 * string param may be encoded as [{id}]. Scalar variables (TextInput etc.)
 * must keep their raw string - wrapping them breaks HyperThread equality checks
 * (e.g. package type {id=Pallet} vs Pallet) and STEPS text variables (script 63). */
const isRecordIdMapping = (item, resourcesList, apiName) => {
  if (item?.is_lookup === true || item?.is_lookup === 1) {
    return true;
  }
  const meta = resolvePagesVariableMeta(resourcesList, item, apiName);
  const fieldTypeName = String(meta?.fieldtype_name || meta?.type || "");
  return (
    fieldTypeName === "Object" ||
    fieldTypeName === "Array" ||
    fieldTypeName === "Messages" ||
    /lookup/i.test(fieldTypeName)
  );
};

/**
 * STEPS text targets (is_lookup=0) often receive Pages Lookup shape [{label,value}].
 * Rest 2.0 check-account compares account_name to a plain string (layout parity).
 * Unwrap only simple single lookup options — never MultiLookup (code), Package
 * rows (_scmRowId/weight), or country picklists ([{id}] only).
 */
export const unwrapStepsScalarLookupSelectedValue = (selectedValue, mappingItem) => {
  if (mappingItem?.is_lookup === true || mappingItem?.is_lookup === 1) {
    return selectedValue;
  }
  if (!Array.isArray(selectedValue) || selectedValue.length !== 1) {
    return selectedValue;
  }
  const entry = selectedValue[0];
  if (entry == null || typeof entry !== "object" || Array.isArray(entry)) {
    return selectedValue;
  }
  if (
    entry.code != null ||
    entry._scmRowId != null ||
    entry.weight != null ||
    entry.length != null ||
    entry.cf_packages != null
  ) {
    return selectedValue;
  }
  const label =
    entry.label != null && entry.label !== ""
      ? entry.label
      : entry.name != null && entry.name !== ""
        ? entry.name
        : null;
  if (label == null) {
    return selectedValue;
  }
  return label;
};

/** Shared pages-config-execute payload builder (button submit + Redux auto-execute). */
export const buildPagesConfigExecutePayload = ({
  fromMapping,
  resourcesList,
  automationType,
  formPayload = {},
  params = {},
}) => {
  const inputMap = Object.fromEntries(
    (resourcesList?.input || []).map((item) => [item.id, item.api_name])
  );

  return (fromMapping || []).map((item) => {
    const apiName = isSet(item?.pages_variable_name, inputMap[item.pages_variable_id]);
    let selectedValue;

    if (apiName) {
      const lowerKey = apiName.toLowerCase();
      if (
        item.pages_variable_name &&
        Object.prototype.hasOwnProperty.call(params, item.pages_variable_name)
      ) {
        const recordIds = params[item.pages_variable_name];
        const recordIdMapping = isRecordIdMapping(item, resourcesList, apiName);
        if (recordIds != null && typeof recordIds === "object") {
          // String arrays are id lists. Arrays of objects (Package, rate rows)
          // and plain maps (selected_rate) stay as the caller sent them.
          if (
            recordIdMapping &&
            Array.isArray(recordIds) &&
            !recordIds.some((entry) => entry != null && typeof entry === "object")
          ) {
            selectedValue = normalizeRecordIdSelectedValue(recordIds);
          } else {
            selectedValue = recordIds;
          }
        } else if (recordIdMapping) {
          selectedValue = normalizeRecordIdSelectedValue(recordIds);
        } else {
          selectedValue = recordIds;
        }
      } else if (Object.prototype.hasOwnProperty.call(params, lowerKey)) {
        selectedValue = params[lowerKey];
      }  else if (
        item.selected_value !== undefined &&
        item.selected_value !== null &&
        item.selected_value !== ""
      ) {
        selectedValue = item.selected_value;
      } else {
        const pagesVarName = item.pages_variable_name;
        const resource = (resourcesList?.input || []).find(
          (r) =>
            (item.pages_variable_id && r.id === item.pages_variable_id) ||
            (apiName &&
              (r.api_name === apiName || r.variable_name === apiName)) ||
            (pagesVarName &&
              (r.api_name === pagesVarName || r.variable_name === pagesVarName))
        );
        const formKeys = [
          pagesVarName,
          apiName,
          lowerKey,
          resource?.api_name,
          resource?.variable_name,
        ]
          .filter(Boolean)
          .map((k) => String(k))
          .filter((k, idx, arr) => arr.indexOf(k) === idx);
        selectedValue = undefined;
        for (let i = 0; i < formKeys.length; i += 1) {
          const fk = formKeys[i];
          const entry = formPayload[fk];
          if (entry == null) {
            continue;
          }
          const candidate =
            entry.selected_value ??
            entry.value ??
            (typeof entry !== "object" ? entry : undefined);
          if (
            candidate !== undefined &&
            candidate !== null &&
            candidate !== ""
          ) {
            selectedValue = candidate;
            break;
          }
        }
      }
    }

    const readFormCandidate = (keys) => {
      for (let i = 0; i < keys.length; i += 1) {
        const entry = formPayload[keys[i]];
        if (entry == null) {
          continue;
        }
        const candidate =
          entry?.selected_value ??
          entry?.value ??
          (entry != null && typeof entry !== "object" ? entry : undefined);
        if (candidate !== undefined && candidate !== null && candidate !== "") {
          return candidate;
        }
      }
      return undefined;
    };

    // Prefer live form postal over country-code ("CA") left in params/shipObj.
    if (
      isPagesPostalVariableKey(apiName) &&
      isLikelyCountryCodeNotPostal(selectedValue)
    ) {
      const postalFormKeys = [
        item.pages_variable_name,
        apiName,
        String(apiName || "").toLowerCase(),
      ].filter(Boolean);
      const formPostal = readFormCandidate(postalFormKeys);
      if (
        formPostal != null &&
        formPostal !== "" &&
        !isLikelyCountryCodeNotPostal(formPostal)
      ) {
        selectedValue = formPostal;
      }
    }

    const apiLower = String(apiName || "").toLowerCase();
    const isPackageApi =
      apiName === "Package" ||
      apiLower === "package" ||
      apiLower === "packages";
    const isAdditionalServicesApi =
      apiLower === "to_additional_services" ||
      apiLower === "from_additional_services" ||
      apiLower === "ship_to_additional_services" ||
      apiLower === "ship_from_additional_services";

    // Prefer live form / host Package over empty/slim rows (dims or description).
    // RATINGS may leave Package as a JSON string; STEPS/D&R need a real array.
    if (isPackageApi) {
      selectedValue = parseAccidentalJsonString(selectedValue);
      let formPkg = parseAccidentalJsonString(
        readFormCandidate([
          item.pages_variable_name,
          apiName,
          apiLower,
          "Package",
          "package",
        ].filter(Boolean))
      );
      if (!Array.isArray(selectedValue) && Array.isArray(formPkg)) {
        selectedValue = formPkg;
      }
      if (Array.isArray(selectedValue)) {
        if (!packageRowsHaveSubformLookupOrDescription(selectedValue)) {
          if (packageRowsHaveSubformLookupOrDescription(formPkg)) {
            selectedValue = formPkg;
          }
        }
        // Blank remount [{weight:""}] must not beat Get Rates dims still in params.
        if (!packageRowsHaveDims(selectedValue)) {
          if (packageRowsHaveDims(formPkg)) {
            selectedValue = formPkg;
          } else {
            const paramKeys = [
              "Package",
              "package",
              "Package_out",
              "package_out",
            ];
            for (let pi = 0; pi < paramKeys.length; pi += 1) {
              const fromParams = parseAccidentalJsonString(params[paramKeys[pi]]);
              if (packageRowsHaveDims(fromParams)) {
                selectedValue = fromParams;
                break;
              }
            }
          }
        }
        selectedValue = normalizePagesSubformPackageRows(selectedValue);
      }
      // D&R Rest body uses package.cf_description; blank → HTTP_400 commodity/description.
      if (automationType === "STEPS" && Array.isArray(selectedValue)) {
        const prodDesc = readFormCandidate([
          "product_description",
          "Product_description",
          "cf_product_description",
        ]);
        const fallbackDesc =
          prodDesc != null && String(prodDesc).trim() !== ""
            ? String(prodDesc).trim()
            : "GENERAL FREIGHT";
        selectedValue = selectedValue.map((row) => {
          if (!row || typeof row !== "object" || Array.isArray(row)) {
            return row;
          }
          const existing =
            row.cf_description != null && String(row.cf_description).trim() !== ""
              ? row.cf_description
              : row.description != null && String(row.description).trim() !== ""
                ? row.description
                : null;
          if (existing != null) {
            return row.cf_description
              ? row
              : { ...row, cf_description: existing };
          }
          return { ...row, cf_description: fallbackDesc, description: fallbackDesc };
        });
        // CREATE/clone: drop persisted package row ids (edit UPDATE keeps them).
        const isCloneSave =
          isTruthyPagesFlag(params.is_clone) ||
          isTruthyPagesFlag(params.pages_clone_mode);
        const isEditSave =
          !isCloneSave &&
          (isTruthyPagesFlag(params.is_edit) ||
            isTruthyPagesFlag(params.pages_edit_mode) ||
            String(params.pages_mode || "").trim().toLowerCase() === "edit");
        if (!isEditSave) {
          selectedValue = stripPersistedSubformRowIds(selectedValue);
        }
      }
      // Dims-only HyperThread shape is for RATINGS only; STEPS/Ship_Now needs
      // description (+ cf_packages) for create-shipment Package subform.
      if (automationType === "RATINGS") {
        selectedValue = normalizePagesHyperthreadPackages(selectedValue);
      }
    }

    // Prefer selected additional services from form when params only have [].
    if (isAdditionalServicesApi) {
      const sanitizedSelected = sanitizePagesAdditionalServices(selectedValue);
      if (!Array.isArray(sanitizedSelected) || sanitizedSelected.length === 0) {
        const formServices = readFormCandidate([
          item.pages_variable_name,
          apiName,
          apiLower,
          apiLower.includes("from")
            ? "from_additional_services"
            : "to_additional_services",
          apiLower.includes("from")
            ? "Ship From Additional Services"
            : "Ship To Additional Services",
        ].filter(Boolean));
        const sanitizedForm = sanitizePagesAdditionalServices(formServices);
        if (Array.isArray(sanitizedForm) && sanitizedForm.length > 0) {
          selectedValue = sanitizedForm;
        }
      }
    }

    if (automationType === "STEPS") {
      selectedValue = unwrapStepsScalarLookupSelectedValue(selectedValue, item);
    }

    const variableMeta = resolvePagesVariableMeta(resourcesList, item, apiName);
    const finalValue = formatPagesExecuteSelectedValue(
      selectedValue,
      variableMeta,
      automationType
    );

    return {
      ...item,
      selected_value: isPagesCheckboxFieldType(variableMeta)
        ? normalizePagesCheckboxSelectedValue(finalValue)
        : isSet(finalValue, ""),
    };
  });
};

export const getPagesSelectAllContext = (source = {}) => {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return null;
  }
  const requiredQuery = source.requiredQuery === true || source.requiredQuery === "true";
  if (!requiredQuery) {
    return null;
  }
  return {
    requiredQuery: true,
    searchKeyword: source.searchKeyword ?? source.search ?? "",
    windowId: source.windowId || "",
    layoutId: source.layoutId || "",
    customViewFilterJsonModel: source.customViewFilterJsonModel || null,
  };
};

/** When Select All is on, POST an object so the server can expand record_ids. Otherwise keep the mapping array. */
export const wrapPagesConfigExecutePayload = (mappings, contextSource) => {
  const ctx = getPagesSelectAllContext(contextSource);
  if (!ctx || !ctx.windowId) {
    return mappings;
  }
  return {
    mappings,
    requiredQuery: ctx.requiredQuery,
    searchKeyword: ctx.searchKeyword,
    windowId: ctx.windowId,
    layoutId: ctx.layoutId,
    customViewFilterJsonModel: ctx.customViewFilterJsonModel,
  };
};

/** Strip JSON quotes from step variable scalar values. */
const normalizeRedirectDataIdValue = (raw) => {
  if (raw == null || raw === undefined) return '';
  return String(raw).replace(/^"+|"+$/g, '').replace(/\\"/g, '').trim();
};

/**
 * Resolve DETAIL/EDIT data id from step response variables.
 * Prefer configured redirect_data_id; if stale (pages-var UUID / empty), fall back
 * to record_id_from_create then record_id — same vars Create Save uses.
 */
export const resolveRedirectDataIdFromVariables = (
  redirect_data_id,
  variables = []
) => {
  const list = Array.isArray(variables) ? variables : [];
  const byId = list.find((v) => v && v.id === redirect_data_id);
  let dataId = normalizeRedirectDataIdValue(byId?.value);
  if (dataId) return dataId;
  const byApi = (apiName) =>
    list.find((v) => {
      if (!v) return false;
      const name = isSet(v.api_name, v.apiName);
      return name === apiName && normalizeRedirectDataIdValue(v.value) !== '';
    });
  const fallback =
    byApi('record_id_from_create') || byApi('record_id') || null;
  return normalizeRedirectDataIdValue(fallback?.value);
};

/**
 * Resolve post-automation redirect URL from page configuration redirect fields.
 * Mirrors Action Button redirect logic in LayoutForms / DetailView.
 */
export const resolveRedirectUrl = (redirectConfig, variables = []) => {
  if (!redirectConfig) return null;
  const {
    redirect_type,
    redirect_window_id,
    redirect_data_id,
    redirect_url_expression,
  } = redirectConfig;

  if (!isSet(redirect_type, '')) return null;

  if (redirect_type === 'OTHER') {
    const customUrl = isSet(redirect_url_expression, null);
    return customUrl !== null && customUrl !== '' ? customUrl : null;
  }

  if (!ShipConstants.REDIRECT_URLS[redirect_type]) return null;

  const dataId = resolveRedirectDataIdFromVariables(redirect_data_id, variables);

  const windowNameObj = getWindowDataUsingId(redirect_window_id);
  const windowObj = getWindowObjectUsingWindowId(redirect_window_id);
  // getWindowDataUsingId returns {} when missing — treat as absent.
  if (!windowObj || !windowNameObj || !windowNameObj.window_id) return null;

  let url = ShipConstants.REDIRECT_URLS[redirect_type];
  url = url.replace('${dataId}', dataId);
  url = url.replace('${windowName}', windowObj.window_name);
  url = url.replace('${windowId}', windowNameObj.window_id);
  url = url.replace('${layoutId}', windowNameObj.layout_id);

  if (redirect_type === 'DETAIL' && isSet(dataId, null) === null) {
    return null;
  }
  return url;
};

/** Navigate via history when redirect URL resolves; returns true if navigation occurred. */
export const resolveAndNavigateRedirect = ({
  redirectConfig,
  variables,
  history,
  onBeforeNavigate,
}) => {
  const url = resolveRedirectUrl(redirectConfig, variables);
  if (!url || !history || typeof history.push !== 'function') return false;
  if (typeof onBeforeNavigate === 'function') {
    onBeforeNavigate();
  }
  history.push(url);
  return true;
};

export const emptyToPageRedirect = () => ({
  redirect_type: '',
  redirect_window_id: '',
  redirect_data_id: '',
  redirect_url_expression: '',
});

const REDIRECT_TYPE_LABELS = {
  EDIT: 'Edit Page',
  DETAIL: 'Detail Page',
  LIST: 'List Page',
};

/** Human-readable redirect target for rule description and redirect field summary. */
export const getRedirectTargetSummary = (
  redirect,
  { windowListOptions = [], outputVariables = [], includeTypeLabel = true } = {}
) => {
  const redirectType = isSet(redirect?.redirect_type, '');
  if (!redirectType) {
    return '';
  }
  if (redirectType === 'OTHER') {
    return isSet(redirect?.redirect_url_expression, '').trim() || 'Custom URL';
  }
  const windowOpt = isSet(windowListOptions, []).find(
    (item) => item.value === redirect?.redirect_window_id
  );
  const varOpt = isSet(outputVariables, []).find(
    (item) => item.id === redirect?.redirect_data_id
  );
  const parts = [];
  if (includeTypeLabel && REDIRECT_TYPE_LABELS[redirectType]) {
    parts.push(REDIRECT_TYPE_LABELS[redirectType]);
  }
  if (windowOpt) {
    parts.push(windowOpt.label);
  }
  if (varOpt && (redirectType === 'EDIT' || redirectType === 'DETAIL')) {
    parts.push(varOpt.variable_name || varOpt.api_name);
  }
  return parts.join(' → ');
};

export const isToPageRedirectMode = (toPage) => {
  if (toPage?.to_page_action_type === 'PAGE') {
    return false;
  }
  if (toPage?.to_page_action_type === 'REDIRECT') {
    return true;
  }
  return toPage?.redirect != null;
};

export const isPlaceholderRulePageId = (pageId) =>
  isSet(pageId, '').startsWith('__rule_');

/** Page ids already used by other open-page rules (excludes redirect rules and placeholders). */
export const getUsedOpenPageIdsForRules = (toPages = [], excludeIndex) =>
  isSet(toPages, [])
    .filter((rule, idx) => idx !== excludeIndex && !isToPageRedirectMode(rule))
    .map((rule) => isSet(rule.page_id, '').trim())
    .filter((id) => id && id !== 'SAME_PAGE' && !isPlaceholderRulePageId(id));

const stripUiOnlyToPageFields = (entry = {}) => {
  const { to_page_action_type, ...clean } = entry;
  return clean;
};

/** Normalize one to_page_mapping entry to nested redirect shape. */
export const normalizeToPageEntry = (entry = {}) => {
  const field_mappings = isSet(entry?.field_mappings, []);
  const rawCondition = isSet(entry?.condition, []);
  const isRedirect = isToPageRedirectMode(entry);
  const condition = isRedirect
    ? rawCondition
    : rawCondition.map((item) => ({
        ...item,
        redirect_mapping_id: null,
      }));
  if (isRedirect && entry.redirect != null && typeof entry.redirect === 'object') {
    return stripUiOnlyToPageFields({ ...entry, field_mappings, condition });
  }
  return stripUiOnlyToPageFields({ ...entry, field_mappings, condition, redirect: null });
};

/** Normalize full to_page_mapping list to nested redirect shape. */
export const normalizeToPageMappingList = (data) =>
  isSet(data?.to_page_mapping, []).map(normalizeToPageEntry);

/**
 * Same-page child automations must merge response into the host page entry
 * (e.g. Add_shipment_onload), not the child config key.
 */
const isExplicitSamePageAutomation = (automationPageDetails) => {
  const sameFlag = automationPageDetails?.is_same_page;
  return (
    sameFlag === true ||
    sameFlag === 1 ||
    sameFlag === "1" ||
    sameFlag === "true"
  );
};

/** True when the router is on a Pages screen (not layout/settings/etc.). */
export const isActivePagesRoute = (pathname) => {
  const path =
    pathname != null && pathname !== ""
      ? String(pathname)
      : typeof window !== "undefined" && window.location
        ? window.location.pathname || ""
        : "";
  return path.startsWith("/pages/") || path.startsWith("/public-pages/");
};

/**
 * After execute, stay on the host only for the mapping that targets it.
 * A second mapping (Tracking Success → results page) must still navigate.
 * An explicit is_same_page flag keeps Add Shipment child steps on the host.
 * Get Rates redirects are never same-page.
 * Same-page updates apply only while the URL is a Pages route — cached pageData
 * from a prior visit must not block navigation from list/settings/window routes.
 */
export const isEffectiveSamePageResult = ({
  automationPageDetails,
  hostPagesConfig,
  toMap,
  getRatesRedirect = false,
  pathname,
}) => {
  if (getRatesRedirect) return false;
  if (!isActivePagesRoute(pathname)) return false;
  if (isExplicitSamePageAutomation(automationPageDetails)) return true;
  const hostPageId = hostPagesConfig?.pages?.id ?? hostPagesConfig?.id ?? null;
  return hostPageId != null && toMap?.page_id === hostPageId;
};

/** True when automation updates the currently open page (flag or to-mapping page_id match). */
export const resolveIsSamePageAutomation = (
  automationPageDetails,
  hostPagesConfig,
  pathname
) => {
  if (!automationPageDetails) return false;
  if (!isActivePagesRoute(pathname)) return false;
  const sameFlag = automationPageDetails.is_same_page;
  if (sameFlag === true || sameFlag === 1 || sameFlag === "1" || sameFlag === "true") {
    return true;
  }
  const hostPageId = hostPagesConfig?.pages?.id ?? hostPagesConfig?.id ?? null;
  if (!hostPageId) return false;
  return isSet(automationPageDetails.to_page_mapping, []).some(
    (entry) => entry?.page_id === hostPageId
  );
};

export const resolveSamePageHostConfig = (pageFlows, automationConfigApiName) => {
  const flows = pageFlows || {};
  const data = flows.pageData || {};
  const activeKey = flows.activePage || flows.activeWindowPage;
  if (activeKey && data[activeKey]?.pages?.html) {
    return { configKey: activeKey, pages: data[activeKey].pages };
  }
  const hostKey = Object.keys(data).find((key) => data[key]?.pages?.html);
  if (hostKey) {
    return { configKey: hostKey, pages: data[hostKey].pages };
  }
  if (automationConfigApiName && data[automationConfigApiName]?.pages?.html) {
    return {
      configKey: automationConfigApiName,
      pages: data[automationConfigApiName].pages,
    };
  }
  return {
    configKey: automationConfigApiName || activeKey || null,
    pages: null,
  };
};

/** Build outputResponse map from automation variables (api_name -> variable). */
export const buildOutputResponseFromVariables = (variables = []) => {
  const outputResponse = {};
  (variables || []).forEach((v) => {
    outputResponse[isSet(v.api_name, v.apiName)] = v;
  });
  return outputResponse;
};

/** Prefer REDIRECT to-mappings before PAGE so success DETAIL is not skipped. */
export const orderToPageMappingForExecute = (toMapping = []) => {
  const list = isSet(toMapping, []);
  const redirects = [];
  const pages = [];
  list.forEach((entry) => {
    if (isToPageRedirectMode(entry)) {
      redirects.push(entry);
    } else {
      pages.push(entry);
    }
  });
  return redirects.concat(pages);
};

/** Evaluate pages_message conditions (same logic as To Mapping rules check). */
export const evaluatePagesMessageCondition = (rules = [], outputResponse = {}) => {
  const safeRules = isSet(rules, []);
  let validationResult = "(true===true)";
  if (safeRules.length > 0) {
    validationResult += " && (";
  }
  const pagesMessage = outputResponse.pages_message;
  const pagesMessageValue = normalizeRedirectDataIdValue(
    pagesMessage?.value ?? pagesMessage?.selected_value
  );
  safeRules.forEach((rule, ruleIndex) => {
    const conditionField = {
      isExist: isSet(pagesMessage, null) !== null,
      field: {
        type: "TextInput",
        ...pagesMessage,
        selectedValue: pagesMessageValue,
      },
      selectedValue: pagesMessageValue,
    };
    if (conditionField.isExist === true) {
      if (ruleIndex > 0) {
        if (isSet(rule.logical_operator, "") === "AND") {
          validationResult += " &&";
        } else if (isSet(rule.logical_operator, "") === "OR") {
          validationResult += " || ";
        }
      }
      validationResult +=
        " (true===" +
        conditionRulesChecking(conditionField, {
          values: rule.value,
          filter: isSet(rule.filter_name, "is not empty"),
        }) +
        " )";
    } else {
      const filterName = isSet(rule.filter_name, "is not empty");
      const treatMissingAsEmpty =
        filterName === "is empty" ||
        filterName === "is null" ||
        (filterName === "equals" && (rule.value === "" || rule.value == null));
      if (ruleIndex > 0) {
        if (isSet(rule.logical_operator, "") === "AND") {
          validationResult += " &&";
        } else if (isSet(rule.logical_operator, "") === "OR") {
          validationResult += " || ";
        }
      }
      validationResult += treatMissingAsEmpty ? " (true===true) " : "(true===false)";
    }
  });
  if (safeRules.length > 0) {
    validationResult += ")";
  }
  try {
    if (eval("(" + validationResult + ")")) {
      return true;
    }
  } catch (e) {
    // ignore eval errors
  }
  return false;
};

/** Build pages-config-execute payload for Lookup / MultiLookup (search, pagination, filterVar). */
export const buildLookupExecutePayload = ({
  fromMapping,
  inputMap,
  config,
  fieldFormPayload,
  search = "",
  page = 0,
  perPage = 10,
}) => {
  const searchField = config?.searchField || "";
  const offsetField = config?.offset || "";
  const limitField = config?.limit || "";
  const filterFields = config?.filterFields || [];

  return fromMapping.map((item) => {
    const apiName = isSet(item?.pages_variable_name, inputMap[item.pages_variable_id]);

    let selectedValue = "";
    if (apiName === searchField) {
      selectedValue = search;
    } else if (apiName === offsetField) {
      selectedValue = page.toString();
    } else if (apiName === limitField) {
      selectedValue = perPage.toString();
    } else if (filterFields.length > 0 && filterFields.includes(apiName)) {
      selectedValue = formatExecuteSelectedValue(
        getFilterPayloadValue(fieldFormPayload, apiName)
      );
    }

    return {
      ...item,
      selected_value: isSet(selectedValue, ""),
    };
  });
};

/** Pages lookup selected value — record_id only (never record.id) */
export const getLookupRecordId = (record, recordIdField) => {
  if (record == null || typeof record !== "object" || Array.isArray(record)) {
    return "";
  }

  let field =
    typeof recordIdField === "string" ? recordIdField.trim() : "";
  if (!field || field.toLowerCase() === "id") {
    field = LOOKUP_RECORD_ID_FIELD;
  }

  let raw;
  if (field.includes(".")) {
    const keys = field.split(".").filter(Boolean);
    if (!keys.length) return "";
    raw = keys.reduce((obj, key) => {
      if (obj == null || typeof obj !== "object") return undefined;
      return obj[key];
    }, record);
  } else {
    raw = record[field];
  }

  if (raw == null || raw === "") return "";
  return String(raw);
};

/** Table row id (record.id) — used as lookup/multilookup `id` */
export const getLookupRowId = (record) => {
  if (record == null || typeof record !== "object" || Array.isArray(record)) {
    return "";
  }
  const raw = record.id;
  return raw != null && raw !== "" ? String(raw) : "";
};

export const buildLookupSelection = (record, recordIdField, label) => {
  const value = getLookupRecordId(record, recordIdField);
  const id = getLookupRowId(record);
  if (!value) return null;
  const labelStr = label != null && label !== "" ? String(label) : "";
  return { id: id || value, label: labelStr, value };
};

export const buildMultiLookupSelection = (record, recordIdField, label) => {
  const item = buildLookupSelection(record, recordIdField, label);
  return item ? { ...item, text: item.label } : null;
};

const hasLookupLabel = (obj) =>
  obj != null && typeof obj === "object" && ("label" in obj || "text" in obj);

const isLookupItem = (item) =>
  item != null && typeof item === "object" && ("value" in item || hasLookupLabel(item));

const coerceToLookupEntry = (obj) => {
  if (obj == null || typeof obj !== "object" || Array.isArray(obj)) return null;
  if (hasLookupLabel(obj) || "value" in obj) return obj;

  // Pages automation/steps sometimes return raw records like {record_id, CompanyName}
  // or {recordId, name}. Convert them into {label, value} so Lookup/MultiLookup can render.
  const rawValue =
    obj.record_id ??
    obj.recordId ??
    obj.recordID ??
    obj.value ??
    obj.id;
  if (rawValue == null || rawValue === "") return null;

  const value = String(rawValue);
  const id = obj.id != null && obj.id !== "" ? String(obj.id) : "";

  // Label is intentionally left empty here because we can't know
  // which field is the "display label" without the lookup config.
  // Components (Lookup/MultiLookup) should use config.defaultField / resultFields for label.
  return { id, value };
};

/** Address-book Taligate Status field ids (Addresses window) — same set as Layout. */
export const TAILGATE_ADDRESS_DESCRIPTOR_IDS = [
  "0ebd0fde-a787-4715-88a3-110d215f806f",
  "26c033f3-45e9-4ba1-a4ef-ba9e0ee99176",
  "98c9912d-6df6-45ba-9ae5-8bcdaa6bde70",
  "a266d499-7ac6-4933-a687-94f4bb2a69bc",
  "cfb1730e-a59b-4834-b90e-304cb0dbd423",
  "f2f4ca5c-cf45-422d-99f1-221f645b03e5",
];

/**
 * Default preset when markup omits tailgatePreset attrs (Add Shipment to_address).
 * Prefer parseTailgatePresetFromAttributes + SQL attrs when available.
 */
export const getDefaultToAddressTailgatePreset = () => ({
  enabled: true,
  servicesField: "to_additional_services",
  serviceCode: "TLGDEL",
  serviceName:
    getHardCodedIds("tailgateDeliveryAuthorizedLabel") ||
    "Tailgate Delivery Authorized",
  serviceId: getHardCodedIds("tailgateDeliveryAuthorizedId") || "",
  statusFields: [
    "cf_taligate_status",
    "cf_tailgate_status",
    ...TAILGATE_ADDRESS_DESCRIPTOR_IDS,
  ],
});

/**
 * Pages version of Layout tailgate preset: address status drives additional services.
 * Returns the next MultiLookup value array, or null when nothing changes.
 */
export const applyTailgatePresetToServices = (
  currentValue,
  addressRecord,
  preset
) =>
  applyTailgatePresetToServicesUtil(
    currentValue,
    addressRecord,
    preset || getDefaultToAddressTailgatePreset()
  );

/**
 * Resolve a To-Mapping field's value from automation output variables.
 * Mappings may carry a collection path "rootId.leafId...": find the root
 * variable by id, parse its value, then walk the stored name path
 * (iterating array elements). Stored segments may be display names
 * ("Carrier Name") while runtime values are keyed by api_name
 * ("carrier_name") — resolve the actual key per element.
 * Returns undefined when the root variable is not in the output.
 */
export const resolveMappedVariableValue = (mField, variables = []) => {
  const rootVariableId = isSet(mField?.automation_variable_id, '').split('.')[0];
  const result = (variables || []).find((item) => item.id === rootVariableId);
  if (!result) return undefined;
  const selectedRaw = result.selected_value ?? result.selectedValue;
  const valueRaw = result.value ?? result.Value;
  const selectedIsBlank =
    selectedRaw == null ||
    selectedRaw === "" ||
    (typeof selectedRaw === "string" &&
      /^\$\{[0-9a-f-]{36}\}$/i.test(selectedRaw.trim()));
  let parsedValue = !selectedIsBlank ? selectedRaw : isSet(valueRaw, "");
  try {
    if (typeof parsedValue === "string") {
      const trimmed = parsedValue.trim();
      if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
        parsedValue = JSON.parse(parsedValue);
      } else if (
        /^\$\{[0-9a-f-]{36}\}$/i.test(trimmed) &&
        selectedRaw != null &&
        selectedRaw !== ""
      ) {
        parsedValue =
          typeof selectedRaw === "string" &&
          (selectedRaw.trim().startsWith("[") || selectedRaw.trim().startsWith("{"))
            ? JSON.parse(selectedRaw)
            : selectedRaw;
      }
    }
  } catch (e) {
    // Ignore parsing error, keep original value
  }
  parsedValue = normalizeOutputResponseValue(parsedValue);
  const resolveValueKey = (obj, key) => {
    if (!obj || typeof obj !== 'object') return key;
    if (Object.prototype.hasOwnProperty.call(obj, key)) return key;
    const snakeKey = key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[\s-]+/g, '_').toLowerCase();
    if (Object.prototype.hasOwnProperty.call(obj, snakeKey)) return snakeKey;
    const match = Object.keys(obj).find((k) => k.toLowerCase() === key.toLowerCase() || k.toLowerCase() === snakeKey);
    return match !== undefined ? match : key;
  };
  const namePath = isSet(mField?.automation_variable_name, '').split('.').slice(1);
  namePath.forEach((key) => {
    if (Array.isArray(parsedValue)) {
      parsedValue = parsedValue.map((el) => (el && typeof el === 'object') ? el[resolveValueKey(el, key)] : el);
    } else if (parsedValue && typeof parsedValue === 'object') {
      parsedValue = parsedValue[resolveValueKey(parsedValue, key)];
    } else {
      parsedValue = undefined;
    }
  });
  return parsedValue;
};

/** Picklist cells (e.g. package uom) from step output may be { name, value }. */
export const unwrapPicklistScalar = (raw) => {
  if (raw == null || Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    if (raw.value != null && raw.value !== "") return raw.value;
    if (raw.name != null && raw.name !== "") return raw.name;
    if (raw.label != null && raw.label !== "") return raw.label;
    return raw;
  }
  return raw;
};

const normalizePicklistFieldsInRow = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return row;
  const next = { ...row };
  ["uom", "UOM", "cf_uom", "cf_unit_of_measure"].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(next, key)) {
      next[key] = unwrapPicklistScalar(next[key]);
    }
  });
  return next;
};

/** Unwrap pages-variable / nested wrappers; keep arrays and lookup objects intact */
export const normalizeOutputResponseValue = (raw) => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return raw;
  if ("selected_value" in raw) return normalizeOutputResponseValue(raw.selected_value);
  if ("value" in raw) {
    const inner = raw.value;
    if (inner != null && typeof inner === "object") return inner;
    if (hasLookupLabel(raw)) return raw;
    return inner;
  }
  return raw;
};

/** Unwrap picklist objects inside package/subform row arrays from automation output. */
export const normalizeAutomationOutputValue = (key, value) => {
  let normalized = normalizeOutputResponseValue(value);
  if (normalized == null) return normalized;
  const keyLower = String(key || "").toLowerCase();
  const isPackageSubformCollection =
    keyLower === "package" ||
    keyLower === "packages" ||
    keyLower === "cf_packages" ||
    keyLower === "package_out";
  // RATINGS may store Package as a JSON string in pageFlows; parse for subform remount.
  if (isPackageSubformCollection && typeof normalized === "string") {
    normalized = parseAccidentalJsonString(normalized);
  }
  if (isPackageSubformCollection && Array.isArray(normalized)) {
    const rows = normalizePagesSubformPackageRows(
      normalized.map(normalizePicklistFieldsInRow).filter((row) => row != null)
    );
    const withoutEmpty = filterEmptyPagesPackageSubformRows(rows);
    const ordered =
      withoutEmpty.length > 0 ? withoutEmpty : rows;
    return sortPagesSubformRowsLikeLayout(ordered);
  }
  if (keyLower === "shipment_date" || keyLower === "shipment date") {
    return normalizePagesRatingsShipmentDateForExecute(normalized);
  }
  if (
    keyLower === "package_type" ||
    keyLower === "package / pallet" ||
    keyLower === "package_pallet"
  ) {
    return normalizePicklistPackageType(normalized) ?? normalized;
  }
  if (
    keyLower === "from_additional_services" ||
    keyLower === "to_additional_services" ||
    keyLower === "ship from additional services" ||
    keyLower === "ship to additional services"
  ) {
    return normalizePagesAdditionalServices(
      sanitizePagesAdditionalServices(normalized)
    );
  }
  if (Array.isArray(normalized)) {
    return normalized.map((row) =>
      row && typeof row === "object" && !Array.isArray(row)
        ? normalizePicklistFieldsInRow(row)
        : row
    );
  }
  return normalized;
};

const resolveLookupEntry = (selected) => {
  if (selected == null) return null;
  if (Array.isArray(selected)) {
    const found = selected.find(isLookupItem);
    if (found) return found;
    const recordLike = selected.find((it) => coerceToLookupEntry(it) != null);
    return recordLike ? coerceToLookupEntry(recordLike) : null;
  }
  if (typeof selected !== "object") {
    const str = String(selected);
    return str ? { label: "", value: str, id: "" } : null;
  }
  if ("selected_value" in selected) return resolveLookupEntry(selected.selected_value);
  if ("value" in selected) {
    const inner = selected.value;
    if (inner != null && typeof inner === "object") return resolveLookupEntry(inner);
    if (hasLookupLabel(selected)) return selected;
    return inner != null && inner !== ""
      ? { label: "", value: String(inner), id: selected.id != null ? String(selected.id) : "" }
      : null;
  }
  if (hasLookupLabel(selected)) return selected;
  return coerceToLookupEntry(selected);
};

export const getLookupStoredValue = (selected) => {
  const entry = resolveLookupEntry(selected);
  if (entry?.value != null && entry.value !== "") return String(entry.value);
  if (typeof selected === "string" || typeof selected === "number") return String(selected);
  return "";
};

export const getLookupStoredLabel = (selected) => {
  const entry = resolveLookupEntry(selected);
  const label = entry?.label ?? entry?.text;
  return label != null && label !== "" ? String(label) : "";
};

/**
 * All identity keys for a Pages lookup (id, value, record_id, nested/array).
 * Onload Account often uses UUID as value; Account_lookup uses record_id as value
 * with UUID as id — both must count as the same selection for clearFields skip.
 */
export const collectLookupIdentityKeys = (selected) => {
  const keys = new Set();
  const add = (v) => {
    if (v == null || v === "") return;
    const s = String(v).trim();
    if (s && s !== "null" && s !== "undefined") keys.add(s);
  };
  if (selected == null) return keys;
  if (Array.isArray(selected)) {
    selected.forEach((item) => {
      collectLookupIdentityKeys(item).forEach((k) => keys.add(k));
    });
    return keys;
  }
  if (typeof selected !== "object") {
    add(selected);
    return keys;
  }
  add(selected.id);
  add(selected.value);
  add(selected.record_id);
  const entry = resolveLookupEntry(selected);
  if (entry) {
    add(entry.id);
    add(entry.value);
    add(entry.record_id);
  }
  // Layout/pages select often nests the record under lookupData
  if (selected.lookupData && typeof selected.lookupData === "object") {
    add(selected.lookupData.id);
    add(selected.lookupData.value);
    add(selected.lookupData.record_id);
    add(selected.lookupData.recordId);
  }
  add(getLookupStoredValue(selected));
  return keys;
};

/** True when prev/next refer to the same lookup record (any overlapping id/value). */
export const isSamePagesLookupIdentity = (prevSelected, nextSelected) => {
  const prevKeys = collectLookupIdentityKeys(prevSelected);
  const nextKeys = collectLookupIdentityKeys(nextSelected);
  if (!prevKeys.size || !nextKeys.size) return false;
  for (const k of prevKeys) {
    if (nextKeys.has(k)) return true;
  }
  return false;
};

/** Stable canonical form for filterVar change detection (string, number, array, object). */
const normalizeFilterValueForCompare = (value) => {
  if (value == null) return "";

  if (typeof value !== "object") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map(normalizeFilterValueForCompare)
      .sort()
      .join("|");
  }

  const unwrapped = normalizeOutputResponseValue(value);
  if (unwrapped !== value) {
    return normalizeFilterValueForCompare(unwrapped);
  }

  const lookupValue = getLookupStoredValue(value);
  if (lookupValue) {
    return lookupValue;
  }

  return Object.keys(value)
    .sort()
    .map((key) => `${key}:${normalizeFilterValueForCompare(value[key])}`)
    .join("|");
};

export const hasFilterPayloadChanged = (prevPayload, nextPayload, filterFields = []) => {
  if (!filterFields.length) return false;

  return filterFields.some((name) => {
    const prevVal = normalizeFilterValueForCompare(getFilterPayloadValue(prevPayload, name));
    const nextVal = normalizeFilterValueForCompare(getFilterPayloadValue(nextPayload, name));
    return prevVal !== nextVal;
  });
};

/**
 * Extract children from Icons tag
 * @param {Array} children - Array of parsed XML/HTML nodes
 * @returns {Array|null} - Children of Icons tag or null if not found
 */
export const extractLookupIcon = (children) => {
  if (!children || !Array.isArray(children)) return null;

  // Find Icons tag
  const iconsNode = children.find(
    (node) => {
      if (!(node.type === "tag" && node.name)) return false;
      const nodeName = node.name.toLowerCase();
      // Support both legacy <Icons> and namespaced <scm:icons>
      return nodeName === "icons" || nodeName.endsWith(":icons");
    }
  );

  if (!iconsNode || !iconsNode.children) return null;

  // Return the children of Icons tag
  return iconsNode.children;
};

/**
 * Helper method to extract text content from a node
 * @param {Object} node - Parsed XML/HTML node
 * @returns {String} - Text content from the node
 */
export const getTextContent = (node) => {
  if (!node || !node.children) return "";
  
  return node.children
    .filter(child => child.type === "text")
    .map(child => child.data || "")
    .join("")
    .trim();
};

/**
 * Extract config attributes from config tag (nested structure)
 * @param {Array} children - Array of parsed XML/HTML nodes
 * @returns {Object|null} - Extracted config object or null if not found
 */
export const extractConfigAttributes = (children) => {
  if (!children || !Array.isArray(children)) {
    return null;
  }

  // Find config tag (case-insensitive)
  const configNode = children.find(
    (node) => {
      const isTag = node.type === "tag";
      const nodeName = (node.name || "").toLowerCase();
      // Support both legacy <Config> and namespaced <scm:config>
      const nameMatches = nodeName === "config" || nodeName.endsWith(":config");
      return isTag && nameMatches;
    }
  );

  if (!configNode) {
    return null;
  }

  // Extract action from attributes
  const apiAction = configNode.attribs?.action || configNode.attribs?.apiAction || "";

  // Extract from nested tags
  // First, try to read flattened attributes from <scm:config>
  let searchField = configNode.attribs?.searchVar || configNode.attribs?.searchvar || "";
  let offset = configNode.attribs?.offsetVar || configNode.attribs?.offsetvar || "";
  let limit = configNode.attribs?.limitVar || configNode.attribs?.limitvar || "";
  const filterVarRaw =
    configNode.attribs?.filterVar ||
    configNode.attribs?.filtervar ||
    configNode.attribs?.filter ||
    "";
  const filterFields = parseFilterVarFields(filterVarRaw);
  let resultVar = "";
  let recordIdField = "";
  let defaultField = "";
  let totalRecordVar = "";
  const resultFields = [];

  if (configNode.children && Array.isArray(configNode.children)) {
    configNode.children.forEach((child) => {
      if (child.type === "tag") {
        const tagName = (child.name || "").toLowerCase();

        // Extract Search tag
        // (legacy structure: <Config><Search>search_input</Search>...</Config>)
        if (tagName === "search") {
          searchField = getTextContent(child) || "";
        }

        // Extract Pagination tag
        if (tagName === "pagination" && child.children) {
          child.children.forEach((paginationChild) => {
            if (paginationChild.type === "tag") {
              const paginationTagName = (paginationChild.name || "").toLowerCase();
              if (paginationTagName === "offset") {
                offset = getTextContent(paginationChild) || "";
              } else if (paginationTagName === "limit") {
                limit = getTextContent(paginationChild) || "";
              }
            }
          });
        }

        // Extract Result tag
        // Support both legacy <Result> and namespaced <scm:result>
        if (tagName === "result" || tagName.endsWith(":result")) {
          resultVar = child.attribs?.var || "";
          recordIdField = child.attribs?.recordId || child.attribs?.recordid || "";
          defaultField = child.attribs?.defaultField || child.attribs?.defaultfield || "";
          totalRecordVar = child.attribs?.totalRecord || child.attribs?.totalrecord || "";
          // Extract Field tags from Result - recursively find all Field tags
          
          // Helper function to recursively find all Field tags
          const extractFieldTags = (node) => {
            if (!node || !node.children || !Array.isArray(node.children)) {
              return;
            }
            
            node.children.forEach((fieldChild) => {
              // Check if this is a Field tag
              const fieldName = (fieldChild.name || "").toLowerCase();
              // Support both legacy <Field> and namespaced <scm:field>
              if (fieldChild.type === "tag" && fieldChild.name && (fieldName === "field" || fieldName.endsWith(":field"))) {
                const type =
                  fieldChild.attribs?.type ||
                  fieldChild.attribs?.Type ||
                  fieldChild.attribs?.fieldType ||
                  fieldChild.attribs?.fieldtype ||
                  "";
                const fieldData = {
                  key: fieldChild.attribs?.key || fieldChild.attribs?.Key || "",
                  label: fieldChild.attribs?.label || fieldChild.attribs?.Label || "",
                  type,
                  fieldType: type,
                };
                resultFields.push(fieldData);
              }
              
              // Recursively check children (in case Field tags are nested)
              if (fieldChild.children && Array.isArray(fieldChild.children)) {
                extractFieldTags(fieldChild);
              }
            });
          };
          
          if (child.children && Array.isArray(child.children)) {
            extractFieldTags(child);
          }
        }
      }
    });
    // console.log('resultFields1111',resultFields);
  }

  const extractedConfig = {
    apiAction,
    searchField,
    offset,
    limit,
    filterVar: filterVarRaw,
    filterFields,
    resultVar,
    defaultField,
    recordIdField,
    totalRecordVar,
    resultFields,
  };

  return extractedConfig;
};

/**
 * Convert node children to HTML string
 * @param {Array} children - Array of parsed XML/HTML nodes
 * @returns {String} - HTML string representation of the nodes
 */
export const renderNodeChildren = (children) => {
  if (!children || !Array.isArray(children)) return "";

  return children
    .map((child) => {
      if (child.type === "text") {
        return child.data || "";
      } else if (child.type === "tag") {
        const attributes = Object.entries(child.attribs || {})
          .map(([key, value]) => `${key}="${value}"`)
          .join(" ");

        const childContent = renderNodeChildren(child.children || []);
        return `<${child.name}${attributes ? ` ${attributes}` : ""}>${childContent}</${child.name}>`;
      }
      return "";
    })
    .join("");
};

